import React, { useState, useEffect } from 'react'

import { useCustomDispatch } from '../../hooks'
import { GetSearchHistory, StoreSearchHistory } from '../../services/reducers/pharmacy/other/commonReducerForInitalData'
import { useCommonReducerSelector } from '../../services/selectors/commonReducerSelector'

import { useLocation } from 'react-router-dom'

import * as routeNames from '../../routes/routeName'

export default function SearchInput() {

    const dispatch = useCustomDispatch()
    const location = useLocation()

    const [query, setQuery] = useState('')
    const [localLoading, setLocalLoading] = useState(false)
    const [isOpen, setIsOpen] = useState(false)

    const { getSearchHistoryResponse, storeSearchHistoryResponse } = useCommonReducerSelector()

    const searchData = getSearchHistoryResponse?.data || []
    const searchHistoryData = storeSearchHistoryResponse?.data || []

    useEffect(() => {
        const trimmedQuery = query.trim()

        if (!trimmedQuery) {
            setLocalLoading(false)
            return undefined
        }

        const handler = setTimeout(() => {
            setLocalLoading(true)
            dispatch(GetSearchHistory({ fetchType: 'getSearchHistory', query: trimmedQuery }))
        }, 1000)

        return () => {
            clearTimeout(handler)
        }
    }, [query, dispatch])

    // Clear local loading when API response comes back
    useEffect(() => {
        if (getSearchHistoryResponse?.loading === false) {
            setLocalLoading(false)
        }
    }, [getSearchHistoryResponse?.loading])

    const handleInputChange = (event) => {
        const value = event.target.value
        setQuery(value)

        if (value.trim()) {
            setLocalLoading(true)
            setIsOpen(true)
        } else {
            setLocalLoading(false)
            setIsOpen(false)
        }
    }

    const isSearchModule = (route) => location.pathname.includes(route) ? '_self' : '_blank'

    const handleItemSelect = (item) => {
        setQuery('')
        setIsOpen(false)

        // Update search history
        dispatch(StoreSearchHistory([item, ...searchHistoryData.filter(h => h.id !== item.id)]))

        // Route configuration mapping
        const routeConfig = {
            patient: {
                condition: item.phn && item.id,
                url: `${routeNames.PHARMACY_PATIENT_PROFILE}?phn=${item.phn}&id=${item.id}`,
                target: isSearchModule(routeNames.PHARMACY_PATIENT_PROFILE)
            },
            clinic: {
                condition: item.id,
                url: `${routeNames.pharmacy_search_module}?module=clinic&id=${item.id}`,
                target: isSearchModule(routeNames.pharmacy_search_module)
            },
            pharmacy: {
                condition: item.id,
                url: `${routeNames.pharmacy_search_module}?module=pharmacies&id=${item.id}`,
                target: isSearchModule(routeNames.pharmacy_search_module)
            },
            // ...(profileData?.type === 'pharmacist' && {
            rx: {
                condition: item.rxnum,
                url: `${routeNames.pharmacy_investigate_rx}?rx-number=${item.rxnum}`,
                target: isSearchModule(routeNames.pharmacy_investigate_rx)
            }
            // })
        }

        // Navigate based on item type
        const config = routeConfig[item.type]
        if (config?.condition) {
            window.open(config.url, config.target)
        }
    }

    const handleClearSearch = () => {
        setQuery('')
        setIsOpen(false)
    }

    const handleInputBlur = () => {
        // Delay closing to allow for clicks on dropdown items
        setTimeout(() => setIsOpen(false), 200)
    }

    const handleInputFocus = () => {
        const trimmedQuery = query.trim()

        if (trimmedQuery) {
            setIsOpen(true)

            if (searchData.length === 0 && getSearchHistoryResponse?.loading !== true) {
                setLocalLoading(true)
                dispatch(GetSearchHistory({ fetchType: 'getSearchHistory', query: trimmedQuery }))
            }

            return
        }

        // Only open dropdown if there's search history to show
        if (searchHistoryData.length > 0) {
            setIsOpen(true)
        }
    }

    // Clear all search history
    const clearAllHistory = () => {
        dispatch(StoreSearchHistory([]))
    }

    // Show search history when focused and no query, or show filtered results when query exists and has data, or show loading
    const shouldShowDropdown = isOpen && (query === '' ? searchHistoryData.length > 0 : query !== '')

    return (
        <div className="relative w-full 2xl:max-w-[400px] 2xl:min-w-[400px] max-w-[300px] min-w-[300px] ">
            <div className="relative md:flex hidden">
                <input
                    className="h-12 w-full pr-12 pl-11 text-base focus:outline-none text-gray-900 border border-gray-300 rounded-lg focus:ring-0 focus:ring-transparent focus:border-gray-300 placeholder:text-gray-400 sm:text-sm"
                    placeholder="Search..."
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={handleInputFocus}
                    value={query}
                />
                {/* Search Icon */}
                <svg
                    className=" cursor-pointer absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    onClick={() => {
                        if (query && query.trim()) {
                            setLocalLoading(true)
                            dispatch(GetSearchHistory({ fetchType: 'getSearchHistory', query: query.trim() }))
                        }
                    }}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>

                {query && (
                    <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full  focus:outline-none"
                        onClick={handleClearSearch}
                        aria-label="Clear search"
                    >
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {shouldShowDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg overflow-hidden ">
                    <div className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100">
                        <span className="text-sm font-semibold text-gray-500">
                            {!query && searchHistoryData.length > 0 ? 'Recent Searches' : 'Search Results'}
                        </span>
                        {!query && searchHistoryData.length > 0 && (
                            <button
                                className="text-xs text-blue-600 hover:underline"
                                onClick={clearAllHistory}
                            >
                                Clear All
                            </button>
                        )}
                    </div>

                    <DisplayData
                        data={!query && searchHistoryData.length > 0 ? searchHistoryData : searchData}
                        type={!query && searchHistoryData.length > 0 ? 'searchHistory' : 'searchResults'}
                        handleItemSelect={handleItemSelect}
                        getSearchHistoryResponse={getSearchHistoryResponse}
                        query={query}
                        localLoading={localLoading}
                    />
                </div>
            )}


        </div>
    )
}


const DisplayData = ({ data, type = '', handleItemSelect, getSearchHistoryResponse, query, localLoading }) => {
    // Debug logging
    // Skeleton loader for search results
    const SearchSkeletonLoader = () => (
        <>
            {[1, 2, 3, 4, 5].map((index) => (
                <li key={index} className="flex items-center px-2 py-2 animate-pulse">
                    <div className="w-8 h-8 bg-gray-200 rounded-full mr-3"></div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                            <div className="h-3 bg-gray-200 rounded w-64"></div>
                            <div className="h-4 bg-gray-200 rounded-full w-12"></div>
                        </div>
                        <div className="mt-1">
                            <div className="h-2 bg-gray-200 rounded w-32"></div>
                        </div>
                    </div>
                </li>
            ))}
        </>
    )

    return (
        <ul className="divide-y divide-gray-100 max-h-[300px] shadow-lg overflow-y-auto ">
            {query && (localLoading || getSearchHistoryResponse?.loading === true) ? (
                <SearchSkeletonLoader />
            ) : (
                data?.map((item, index) => (
                    <li
                        key={index}
                        className="flex items-center px-2 py-2 group hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                            handleItemSelect(item)
                        }}
                    >
                        <span className="flex items-center justify-center w-8 h-8 rounded-full mr-3">
                            {item?.type === 'clinic' &&
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-building2 h-4 w-4 text-orange-600"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path><path d="M10 6h4"></path><path d="M10 10h4"></path><path d="M10 14h4"></path><path d="M10 18h4"></path></svg>
                            }
                            {(item?.type === 'pharmacy' || item?.type === 'rx') &&
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pill h-4 w-4 text-green-600"><path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path><path d="m8.5 8.5 7 7"></path></svg>
                            }
                            {item?.type === 'patient' &&
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user h-4 w-4 text-blue-600"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                            }
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 ">
                                <span className="font-semibold text-gray-900 text-sm uppercase leading-[1.2em]">{item?.name || item?.rxnum} {item?.type === 'rx' && <span className='text-gray-500 text-[11px]'>STAT: {item?.rxstat}</span>}
                                    <span className={`px-2 py-0.2 ml-1 text-[11px] font-medium rounded-full ${item.type === 'patient' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                                        item.type === 'clinic' ? 'bg-red-100 text-red-800 border border-red-300' :
                                            'bg-green-100 text-green-800 border border-green-300'
                                        }`}>
                                        {item.type?.toUpperCase()}
                                    </span>
                                </span>

                            </div>
                            <div className="text-[11px] text-gray-600">
                                {item.type === 'patient' && item.phn && (
                                    <span className='block'>PHN: {item.phn}</span>
                                )}
                                {(item.type === 'clinic' || item.type === 'pharmacy') && item.city && (
                                    <span className='capitalize'>{item.city} | </span>
                                )}
                                {(item.type === 'clinic' || item.type === 'pharmacy') && item.phone && (
                                    <span>Ph: {item.phone}</span>
                                )}

                                {item.type === 'rx' && item.rxnum && item.ai_start_date && item.ai_stop_date && (
                                    <span>Duration: {(item.ai_start_date)} {item.ai_start_date && item.ai_stop_date && '-'} {(item.ai_stop_date)}</span>
                                )}

                            </div>
                        </div>
                    </li>
                ))
            )}

            {type === 'searchHistory' && data?.length === 0 && query === '' && (
                <li className="px-4 py-8 text-center text-gray-500">
                    <svg className="mx-auto w-8 h-8 text-gray-300 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <p className="text-sm">No recent searches</p>
                    <p className="text-xs text-gray-400 mt-1">Your search history will appear here</p>
                </li>
            )}

            {query !== '' && data?.length === 0 && type === 'searchResults' && !localLoading && !getSearchHistoryResponse?.loading && (
                <li className="px-4 py-8 text-center text-gray-500">
                    <svg
                        className="mx-auto w-8 h-8 text-gray-400 mb-2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <p className=" text-sm font-semibold text-gray-900">No results found</p>
                    <p className=" text-xs text-gray-500">No components found for this search term. Please try again.</p>
                </li>
            )}
        </ul>
    )
}
